import React from "react";
import { Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom';
import {
    BalanceProvider,
    DistributionProvider,
    ExhibitorProvider,
    SessionProvider,
    TemplateProvider,
    StatementProvider,
    ToastProvider,
    useSession,
    useAuthenticatedSession,
    WindowDimensionsProvider,
    LoaderProvider,
    ParameterProvider,
    PopupProvider
} from './hooks';
import {
    BalancePage,
    DistributionDetailPage,
    DistributionPage,
    HomePage,
    LoginPage,
    ProfilePage,
    RecoverPasswordPage,
    RedemptionPage,
    ResetPasswordPage,
    StatementPage
} from './pages';

type Props = {
    children: React.ReactElement;
}

const PublicRoute: React.FC<Props> = ({ children }) => {
    const { user } = useSession();
    return user ? <Navigate to="/home" replace /> : children;
};

const ProtectedRoute: React.FC<Props> = ({ children }) => {
    const { user } = useSession();
    return user ? children : <Navigate to="/" replace />;
};

const BalanceRoute: React.FC<Props> = ({ children }) => {
    const { user } = useAuthenticatedSession();
    const { pathname } = useLocation();

    if (!user.hasBalancePermission && pathname === '/balance')
        return <Navigate to="/home" replace />;

    return children;
};

const Router: React.FC = () => {
    return (
        <Routes>
            <Route element={(
                <WindowDimensionsProvider>
                    <LoaderProvider>
                        <ToastProvider>
                            <PopupProvider>
                                <Outlet />
                            </PopupProvider>
                        </ToastProvider>
                    </LoaderProvider>
                </WindowDimensionsProvider>
            )}>
                <Route path="redeem" element={<RedemptionPage />} />
                <Route element={(
                    <ExhibitorProvider>
                        <StatementProvider>
                            <BalanceProvider>
                                <TemplateProvider>
                                    <ParameterProvider>
                                        <DistributionProvider>
                                            <SessionProvider>
                                                <Outlet />
                                            </SessionProvider>
                                        </DistributionProvider>
                                    </ParameterProvider>
                                </TemplateProvider>
                            </BalanceProvider>
                        </StatementProvider>
                    </ExhibitorProvider>
                )}>
                    <Route element={(
                        <PublicRoute>
                            <Outlet />
                        </PublicRoute>
                    )}>
                        <Route path="/" element={<LoginPage />} />
                        <Route path="recover-password" element={<RecoverPasswordPage />} />
                        <Route path="reset-password" element={<ResetPasswordPage />} />
                    </Route>
                    <Route element={(
                        <ProtectedRoute>
                            <Outlet />
                        </ProtectedRoute>
                    )
                    }>
                        <Route path="home" element={<HomePage />} />
                        <Route path="statement" element={<StatementPage />} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route element={(
                            <BalanceRoute>
                                <Outlet />
                            </BalanceRoute>
                        )}>
                            <Route path="balance" element={<BalancePage />} />
                        </Route>
                        <Route path="distribution">
                            <Route index element={<DistributionPage />} />
                            <Route path="detail" element={<DistributionDetailPage />} />
                        </Route>
                    </Route>
                </Route>
            </Route>
        </Routes>
    )
};

export default Router;